import React, { FC, useCallback, useEffect, useState, useRef } from 'react';
import { Text, TextVariant } from '@naf/text';
import { StepWizardChildProps } from 'react-step-wizard';
import { Link } from '@naf-simba/elements-links';
import { Loader } from '@naf-simba/building-blocks-loaders';
import { useDispatch } from 'react-redux';
import { useStateMachine } from 'little-state-machine';
import { isAxiosError } from '@shared/apiClient';
import useSelector from '@redux/typedHooks';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import { actions as orderActions } from '@redux/modules/order';
import { useQueryParams } from '@hooks/useQueryParameters';
import { useDocumentData } from '@hooks/Documents/useDocumentData';
import { useAuth0Token } from '@hooks/useAuth0Token';
import { createOrderPayload } from '../../lib/createOrderPayload';
import { hasDocumentFiles } from '../../lib/documentForm.utils';
import { useTrackingPaymentStep } from '../../utils/useIDPTracking';
import { updateAction } from '../../lib/updateAction';
import {
  type DocumentSubmissionResult,
  getCampaignCode,
  getNumberOfProducts,
  getPaymentValidationErrorStep,
  getStatusText,
  IDP_ARTICLE_SLUG,
  ORCHESTRATION_ERROR,
  submitDocumentFormForOrchestration,
  UNKNOWN_GENERAL_ERROR,
  UNKNOWN_SERVER_VALIDATION_ERROR,
  UNKNOWN_VALIDATION_ERROR,
  validatePaymentOrder,
} from './Redirect.utils';
import * as S from './Redirect.styles';

type DocumentOrchestrationResult = { shouldContinue: true; orchestrationId?: string } | { shouldContinue: false };

export const Redirect: FC<Partial<StepWizardChildProps>> = ({ goToStep, currentStep }) => {
  const dispatch = useDispatch();
  const dataLayer = useGTMDataLayer();
  const queryParams = useQueryParams();
  const { simpleToken } = useAuth0Token();
  const [tempError, setTempError] = useState<string | undefined>(undefined);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmittingForms, setIsSubmittingForms] = useState(false);
  const hasStartedRef = useRef(false);
  const isCreatingOrder = useSelector((state) => state.order.isCreatingOrder);
  const paymentOrderUrl = useSelector((state) => state.order.orderDetails?.paymentOrderUrl);
  const isCreated = useSelector((state) => state.order.isCreated);
  const serverError = useSelector((state) => state.order.validation?.validationResults);
  const { baseUrl } = useSelector((state) => state.application);
  const { customerInformation } = useSelector((state) => state.myMembership);

  const {
    actions,
    state: { internationalDrivingPermitForm, idpCustomerForm, idpDocumentForm },
  } = useStateMachine({ updateAction });

  const { data: productArticle } = useDocumentData(IDP_ARTICLE_SLUG, simpleToken) as {
    data: { dynamicForm?: { options?: { endpoint?: string; httpMethod?: string } } };
  };

  const resetValidationState = () => {
    setIsValidating(false);
    hasStartedRef.current = false;
  };

  const returnToDocumentUpload = () => {
    setIsSubmittingForms(false);
    resetValidationState();
    goToStep?.(3);
  };

  const setDocumentValidationError = (validationError: string | undefined) => {
    actions.updateAction({
      formName: 'idpDocumentForm',
      data: { validationError },
    });
    returnToDocumentUpload();
  };

  const createPaymentOrderPayload = (orchestrationId: string) => {
    const hasSelectedMembership = !!internationalDrivingPermitForm?.membership?.productId;
    const campaignCode = getCampaignCode(hasSelectedMembership, queryParams?.campaignCode);

    return createOrderPayload(
      idpCustomerForm,
      internationalDrivingPermitForm,
      `${baseUrl}/kvittering-internasjonalt-forerkort`,
      `${baseUrl}/betaling-kansellert`,
      orchestrationId,
      campaignCode,
      customerInformation?.data?.customerId,
    );
  };

  const startValidationFlow = () => {
    hasStartedRef.current = true;
    dispatch(orderActions.clearValidation());
    setIsValidating(true);
  };

  const handleMissingOrchestrationId = () => {
    setTempError(ORCHESTRATION_ERROR);
    setDocumentValidationError(ORCHESTRATION_ERROR);
  };

  const handleDocumentSubmissionResult = (
    documentSubmissionResult: DocumentSubmissionResult,
  ): DocumentOrchestrationResult => {
    if (documentSubmissionResult.result === 'success') {
      return {
        shouldContinue: true,
        orchestrationId: documentSubmissionResult.orchestrationId,
      };
    }

    if (documentSubmissionResult.result === 'validationError') {
      setDocumentValidationError(documentSubmissionResult.validationError);
      return { shouldContinue: false };
    }

    setTempError(documentSubmissionResult.validationError);
    setDocumentValidationError(documentSubmissionResult.validationError);
    return { shouldContinue: false };
  };

  const submitDocumentsForCurrentOrder = async (): Promise<DocumentOrchestrationResult> => {
    const documentEndpoint = productArticle?.dynamicForm?.options?.endpoint;

    if (!hasDocumentFiles(idpDocumentForm) || !documentEndpoint) {
      return { shouldContinue: true };
    }

    setIsSubmittingForms(true);

    try {
      const documentSubmissionResult = await submitDocumentFormForOrchestration(documentEndpoint, idpDocumentForm);
      return handleDocumentSubmissionResult(documentSubmissionResult);
    } finally {
      setIsSubmittingForms(false);
    }
  };

  const preValidatePaymentOrder = async (paymentOrderPayload: ReturnType<typeof createPaymentOrderPayload>) => {
    const paymentValidationResult = await validatePaymentOrder(paymentOrderPayload);

    if (paymentValidationResult.result === 'success') {
      return true;
    }

    resetValidationState();

    if (paymentValidationResult.result === 'validationError') {
      dispatch(orderActions.createOrder.failure(paymentValidationResult.validationError));
    }

    return false;
  };

  const dispatchPaymentOrder = (paymentOrderPayload: ReturnType<typeof createPaymentOrderPayload>) => {
    setIsValidating(false);

    if (idpCustomerForm && internationalDrivingPermitForm) {
      hasStartedRef.current = false;
      dispatch(orderActions.createOrder.request(paymentOrderPayload));
    }
  };

  const handleUnexpectedValidationError = (error: unknown) => {
    setIsValidating(false);
    setIsSubmittingForms(false);
    setTempError(isAxiosError(error) ? UNKNOWN_VALIDATION_ERROR : UNKNOWN_GENERAL_ERROR);
    hasStartedRef.current = false;
  };

  useEffect(() => {
    if (hasStartedRef.current || !idpCustomerForm || isCreated || isCreatingOrder) {
      return;
    }

    const validateAndSubmit = async () => {
      try {
        startValidationFlow();

        const documentResult = await submitDocumentsForCurrentOrder();

        if (!documentResult.shouldContinue) {
          return;
        }

        if (!documentResult.orchestrationId) {
          handleMissingOrchestrationId();
          return;
        }

        const paymentOrderPayload = createPaymentOrderPayload(documentResult.orchestrationId);
        const isPaymentOrderValid = await preValidatePaymentOrder(paymentOrderPayload);

        if (!isPaymentOrderValid) {
          return;
        }

        dispatchPaymentOrder(paymentOrderPayload);
      } catch (error) {
        handleUnexpectedValidationError(error);
      }
    };

    validateAndSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackCheckoutAction = useCallback(
    (checkoutAction: string) => {
      dataLayer?.push({
        event: NAFGTMEvent.checkoutAction,
        checkout_step: currentStep,
        checkout_action: checkoutAction,
      });
    },
    [currentStep, dataLayer],
  );

  const returnToCustomerFormAfterUnknownServerError = useCallback(() => {
    setTempError(UNKNOWN_SERVER_VALIDATION_ERROR);

    const timeoutId = setTimeout(() => {
      setTempError(undefined);
      trackCheckoutAction('Ukjent valideringsfeil fra server');
      goToStep?.(2);
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [goToStep, trackCheckoutAction]);

  const returnToFirstPaymentValidationError = useCallback(() => {
    trackCheckoutAction('Kjent valideringsfeil fra server');

    const numberOfProducts = getNumberOfProducts(internationalDrivingPermitForm?.product?.amount);
    const hasSelectedMembership = !!internationalDrivingPermitForm?.membership?.productId;
    goToStep?.(getPaymentValidationErrorStep(serverError?.[0]?.field, numberOfProducts, hasSelectedMembership));
  }, [
    goToStep,
    internationalDrivingPermitForm?.membership?.productId,
    internationalDrivingPermitForm?.product?.amount,
    serverError,
    trackCheckoutAction,
  ]);

  useEffect(() => {
    if (hasStartedRef.current) {
      return undefined;
    }

    if (goToStep && dataLayer && currentStep) {
      if (serverError === null) {
        return returnToCustomerFormAfterUnknownServerError();
      }

      if (serverError) {
        returnToFirstPaymentValidationError();
      }
    }

    return undefined;
  }, [
    currentStep,
    dataLayer,
    goToStep,
    returnToCustomerFormAfterUnknownServerError,
    returnToFirstPaymentValidationError,
    serverError,
  ]);

  const trackingStatus = useTrackingPaymentStep(internationalDrivingPermitForm?.product?.productId, currentStep || 4);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !!paymentOrderUrl &&
      (trackingStatus === 'done' || trackingStatus === 'error')
    ) {
      window.location.href = `${paymentOrderUrl}`;
    }
  }, [paymentOrderUrl, trackingStatus]);

  const statusText = getStatusText(isValidating, isSubmittingForms, isCreatingOrder, isCreated, tempError);

  return (
    <>
      {statusText && <S.AnimatedText variant={TextVariant.Header3}>{statusText}</S.AnimatedText>}
      <S.LoaderContainer>
        {isCreated && (
          <div>
            <Text variant={TextVariant.Header3}>Blir den stående og spinne?</Text>
            <Link href={paymentOrderUrl} text="Klikk og prøv en gang til" />
          </div>
        )}
        <S.LoaderWrapper>
          <Loader />
        </S.LoaderWrapper>
      </S.LoaderContainer>
    </>
  );
};
